import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';
import DeliveryMap from './components/DeliveryMap';
import DeliveryCalculator from './components/DeliveryCalculator';

/* ─── DATA ─────────────────────────────────────────────────── */

const deliveryZones = [
  {
    id: 'express',
    icon: 'ri-flashlight-line',
    color: '#ff2d78',
    title: 'Экспресс-доставка',
    subtitle: 'За 15–60 минут',
    price: 'от 199 ₽',
    freeFrom: null,
    time: '15–60 мин',
    zones: ['Москва (в пределах МКАД)', 'Санкт-Петербург (центр)'],
    hours: 'Круглосуточно, 24/7',
    features: ['Курьер на велосипеде или самокате', 'Отслеживание в реальном времени', 'Уведомления в мессенджер', 'Бесконтактная передача'],
    badge: 'БЫСТРЕЕ ВСЕГО',
  },
  {
    id: 'standard',
    icon: 'ri-truck-line',
    color: '#00f5ff',
    title: 'Стандартная доставка',
    subtitle: 'В течение дня',
    price: 'от 99 ₽',
    freeFrom: 500,
    time: '2–4 часа',
    zones: ['Москва и МО', 'Санкт-Петербург и ЛО', 'Казань', 'Екатеринбург', 'Новосибирск'],
    hours: '08:00–22:00',
    features: ['Курьерская доставка до двери', 'Выбор удобного времени', 'Бесплатно от 500 ₽', 'SMS-уведомления'],
    badge: 'ПОПУЛЯРНО',
  },
  {
    id: 'pickup',
    icon: 'ri-store-2-line',
    color: '#39ff14',
    title: 'Самовывоз из аптеки',
    subtitle: 'Готово через 1 час',
    price: 'Бесплатно',
    freeFrom: 0,
    time: '1 час',
    zones: ['Более 1 000 аптек по всей России', '40+ регионов'],
    hours: 'По графику аптеки',
    features: ['Бронирование онлайн', 'Готово через 1 час', 'Оплата при получении', 'Без минимальной суммы'],
    badge: 'БЕСПЛАТНО',
  },
  {
    id: 'post',
    icon: 'ri-mail-send-line',
    color: '#f0ff00',
    title: 'Почта России / СДЭК',
    subtitle: 'По всей России',
    price: 'от 250 ₽',
    freeFrom: 2000,
    time: '3–10 дней',
    zones: ['Все регионы России', 'Отдалённые районы'],
    hours: 'Пн–Сб: 09:00–18:00',
    features: ['Доставка в любую точку РФ', 'Трек-номер для отслеживания', 'Бесплатно от 2 000 ₽', 'Страхование посылки'],
    badge: 'ПО ВСЕЙ СТРАНЕ',
  },
];

const deliveryTariffs = [
  { zone: 'Москва (в пределах МКАД)', express: '199 ₽', standard: 'Бесплатно от 500 ₽', pickup: 'Бесплатно', post: '—' },
  { zone: 'Москва (за МКАД, до 30 км)', express: '299 ₽', standard: '149 ₽ / Бесплатно от 1 000 ₽', pickup: 'Бесплатно', post: '—' },
  { zone: 'Московская область', express: '—', standard: '199 ₽ / Бесплатно от 1 500 ₽', pickup: 'Бесплатно', post: 'от 250 ₽' },
  { zone: 'Санкт-Петербург', express: '199 ₽', standard: 'Бесплатно от 500 ₽', pickup: 'Бесплатно', post: '—' },
  { zone: 'Ленинградская область', express: '—', standard: '199 ₽ / Бесплатно от 1 500 ₽', pickup: 'Бесплатно', post: 'от 250 ₽' },
  { zone: 'Другие города-миллионники', express: '—', standard: '249 ₽ / Бесплатно от 2 000 ₽', pickup: 'Бесплатно', post: 'от 300 ₽' },
  { zone: 'Остальные регионы РФ', express: '—', standard: '—', pickup: 'Бесплатно', post: 'от 350 ₽' },
];

const paymentMethods = [
  {
    id: 'card-online',
    icon: 'ri-bank-card-line',
    color: '#ff2d78',
    title: 'Банковская карта онлайн',
    desc: 'Visa, Mastercard, МИР. Оплата на сайте при оформлении заказа. Безопасный платёж через 3D-Secure.',
    badges: ['Visa', 'Mastercard', 'МИР'],
    instant: true,
  },
  {
    id: 'sbp',
    icon: 'ri-qr-code-line',
    color: '#00f5ff',
    title: 'СБП — Система быстрых платежей',
    desc: 'Оплата по QR-коду через мобильный банк. Мгновенное зачисление, без комиссии.',
    badges: ['СБП', 'QR-код'],
    instant: true,
  },
  {
    id: 'cash',
    icon: 'ri-money-cny-circle-line',
    color: '#39ff14',
    title: 'Наличными курьеру',
    desc: 'Оплата наличными при получении заказа. Курьер выдаёт кассовый чек.',
    badges: ['Наличные', 'Чек'],
    instant: false,
  },
  {
    id: 'card-courier',
    icon: 'ri-secure-payment-line',
    color: '#f0ff00',
    title: 'Картой курьеру',
    desc: 'Оплата банковской картой через терминал курьера. Поддерживаются все карты и Apple/Google Pay.',
    badges: ['Терминал', 'Apple Pay', 'Google Pay'],
    instant: false,
  },
  {
    id: 'bonus',
    icon: 'ri-gift-line',
    color: '#ff6b00',
    title: 'Бонусными баллами',
    desc: 'Оплата до 50% стоимости заказа накопленными бонусными баллами программы лояльности NeonPharma.',
    badges: ['Бонусы', 'До 50%'],
    instant: true,
  },
  {
    id: 'invoice',
    icon: 'ri-file-list-3-line',
    color: '#ff2d78',
    title: 'Счёт для юридических лиц',
    desc: 'Безналичная оплата по счёту для организаций и ИП. Договор, закрывающие документы.',
    badges: ['Юр. лица', 'Счёт-фактура'],
    instant: false,
  },
];

const faqItems = [
  {
    q: 'Как отследить мой заказ?',
    a: 'После оформления заказа вы получите SMS и email с трек-номером. Отслеживание доступно в личном кабинете на сайте или в мобильном приложении в режиме реального времени.',
  },
  {
    q: 'Можно ли изменить адрес доставки после оформления?',
    a: 'Да, изменить адрес можно до момента передачи заказа курьеру. Свяжитесь с нашей службой поддержки по телефону 8 499 653 6 277 или через чат на сайте.',
  },
  {
    q: 'Что делать, если курьер не приехал в указанное время?',
    a: 'Позвоните на горячую линию 8 499 653 6 277. Мы свяжемся с курьером и уточним статус. Если задержка более 30 минут — предоставим скидку 10% на следующий заказ.',
  },
  {
    q: 'Доставляете ли вы рецептурные препараты?',
    a: 'Рецептурные препараты доставляются только при наличии оригинала рецепта. Курьер проверит рецепт при передаче заказа. Без рецепта рецептурные препараты не выдаются.',
  },
  {
    q: 'Как вернуть товар?',
    a: 'Лекарственные препараты надлежащего качества возврату и обмену не подлежат согласно Постановлению Правительства РФ № 55. Возврат возможен только при обнаружении брака или ошибки в комплектации.',
  },
  {
    q: 'Есть ли минимальная сумма заказа?',
    a: 'Минимальная сумма заказа для доставки курьером — 300 ₽. Для самовывоза из аптеки минимальной суммы нет. Бесплатная доставка при заказе от 500 ₽ в пределах МКАД.',
  },
];

const deliverySteps = [
  { step: '01', icon: 'ri-shopping-cart-line', color: '#ff2d78', title: 'Оформите заказ', desc: 'Добавьте товары в корзину и выберите удобный способ доставки' },
  { step: '02', icon: 'ri-bank-card-line', color: '#00f5ff', title: 'Оплатите', desc: 'Выберите удобный способ оплаты — онлайн или при получении' },
  { step: '03', icon: 'ri-notification-3-line', color: '#39ff14', title: 'Получите уведомление', desc: 'SMS и push-уведомление о статусе заказа и времени доставки' },
  { step: '04', icon: 'ri-map-pin-line', color: '#f0ff00', title: 'Получите заказ', desc: 'Курьер доставит заказ до двери или заберите из ближайшей аптеки' },
];

const deliveryJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Доставка и оплата — NeonPharma',
    description: 'Условия доставки лекарств NeonPharma: экспресс за 15 минут, стандартная за 2–4 часа, самовывоз из 1000+ аптек. Способы оплаты.',
    url: `${SITE_URL}/delivery`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Главная', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Доставка и оплата', item: `${SITE_URL}/delivery` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Как отследить мой заказ?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'После оформления заказа вы получите SMS и email с трек-номером. Отслеживание доступно в личном кабинете на сайте или в мобильном приложении в режиме реального времени.',
        },
      },
      {
        '@type': 'Question',
        name: 'Есть ли минимальная сумма заказа?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Минимальная сумма заказа для доставки курьером — 300 ₽. Для самовывоза из аптеки минимальной суммы нет. Бесплатная доставка при заказе от 500 ₽ в пределах МКАД.',
        },
      },
      {
        '@type': 'Question',
        name: 'Доставляете ли вы рецептурные препараты?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Рецептурные препараты доставляются только при наличии оригинала рецепта. Курьер проверит рецепт при передаче заказа.',
        },
      },
    ],
  },
];

/* ─── COMPONENTS ────────────────────────────────────────────── */

const FaqItem = ({ item }: { item: typeof faqItems[0] }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="card-cyber rounded-xl overflow-hidden transition-all duration-300"
      style={{ borderColor: open ? 'rgba(255,45,120,0.3)' : 'rgba(255,255,255,0.06)' }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-5 cursor-pointer text-left"
      >
        <span className="font-orbitron font-bold text-sm text-white pr-4">{item.q}</span>
        <div
          className="w-7 h-7 flex items-center justify-center rounded-full shrink-0 transition-all duration-300"
          style={open ? { background: '#ff2d78', boxShadow: '0 0 10px #ff2d7860' } : { background: 'rgba(255,255,255,0.06)' }}
        >
          <i className={`text-sm transition-transform duration-300 ${open ? 'ri-subtract-line text-white' : 'ri-add-line text-white/40'}`} />
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5">
          <div className="h-px mb-4" style={{ background: 'rgba(255,45,120,0.15)' }} />
          <p className="font-mono text-xs text-white/50 leading-relaxed">{item.a}</p>
        </div>
      )}
    </div>
  );
};

/* ─── PAGE ──────────────────────────────────────────────────── */

const DeliveryPage = () => {
  const [activeZone, setActiveZone] = useState('standard');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const activeZoneData = deliveryZones.find((z) => z.id === activeZone)!;

  return (
    <div className="min-h-screen" style={{ background: '#050508' }}>
      <SeoHead
        title="Доставка и оплата лекарств — NeonPharma | Экспресс за 15 минут"
        description="Доставка лекарств NeonPharma: экспресс за 15–60 минут, стандартная за 2–4 часа, самовывоз из 1000+ аптек. Бесплатная доставка от 500 ₽. Оплата картой, СБП, наличными."
        keywords="доставка лекарств, экспресс доставка аптека, самовывоз аптека, оплата лекарств"
        canonical="/delivery"
        jsonLd={deliveryJsonLd}
      />
      <Navbar />

      {/* ── Hero ── */}
      <section className="relative pt-28 pb-14 px-6 lg:px-12 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/0f8f29db9cb7e21cf569fc8de8c6c531.jpg"
            alt="Доставка"
            className="w-full h-full object-cover object-top opacity-12"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-[#050508]" />
        </div>
        <div className="absolute top-10 left-1/3 w-96 h-64 rounded-full opacity-8" style={{ background: '#00f5ff', filter: 'blur(100px)' }} />
        <div className="absolute top-0 right-1/4 w-72 h-72 rounded-full opacity-8" style={{ background: '#ff2d78', filter: 'blur(90px)' }} />

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 font-mono text-xs text-white/30 mb-8">
            <Link to="/" className="hover:text-neon-cyan transition-colors">Главная</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-neon-cyan">Доставка и оплата</span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: 'rgba(0,245,255,0.12)', border: '1px solid rgba(0,245,255,0.35)' }}>
                  <i className="ri-truck-line text-lg" style={{ color: '#00f5ff' }} />
                </div>
                <p className="font-mono text-xs tracking-[0.3em] text-neon-cyan/70">УСЛОВИЯ ДОСТАВКИ</p>
              </div>
              <h1 className="font-orbitron font-black text-4xl md:text-6xl text-white leading-tight">
                ДОСТАВКА<br />
                <span style={{ color: '#00f5ff', textShadow: '0 0 30px #00f5ff80' }}>И ОПЛАТА</span>
              </h1>
              <p className="font-mono text-sm text-white/40 mt-3 max-w-lg leading-relaxed">
                Доставляем лекарства по всей России. Экспресс за 15 минут, стандартная за 2–4 часа, самовывоз из 1 000+ аптек.
              </p>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { val: '15 мин', label: 'Экспресс-доставка', color: '#ff2d78', icon: 'ri-flashlight-line' },
                { val: '500 ₽', label: 'Бесплатная доставка', color: '#39ff14', icon: 'ri-gift-line' },
                { val: '1 000+', label: 'Аптек для самовывоза', color: '#00f5ff', icon: 'ri-store-2-line' },
                { val: '24/7', label: 'Работаем без выходных', color: '#f0ff00', icon: 'ri-time-line' },
              ].map((s) => (
                <div key={s.label} className="px-4 py-3 rounded-xl text-center" style={{ background: `${s.color}08`, border: `1px solid ${s.color}20` }}>
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg mx-auto mb-1.5" style={{ background: `${s.color}15` }}>
                    <i className={`${s.icon} text-sm`} style={{ color: s.color }} />
                  </div>
                  <div className="font-orbitron font-black text-lg leading-none" style={{ color: s.color, textShadow: `0 0 8px ${s.color}60` }}>{s.val}</div>
                  <div className="font-mono text-[10px] text-white/30 mt-1 leading-tight">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="px-6 lg:px-12 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10">
            <p className="font-mono text-xs tracking-[0.3em] text-neon-cyan/60 mb-2">КАК ЭТО РАБОТАЕТ</p>
            <h2 className="font-orbitron font-black text-2xl md:text-3xl text-white">4 ПРОСТЫХ ШАГА</h2>
            <div className="mt-2 w-12 h-0.5" style={{ background: 'linear-gradient(90deg, #00f5ff, transparent)' }} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {deliverySteps.map((s, i) => (
              <div key={s.step} className="relative">
                <div className="card-cyber rounded-2xl p-5 h-full" style={{ borderColor: `${s.color}20` }}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-orbitron font-black text-3xl" style={{ color: `${s.color}30` }}>{s.step}</span>
                    <div className="w-9 h-9 flex items-center justify-center rounded-xl" style={{ background: `${s.color}15`, border: `1px solid ${s.color}40` }}>
                      <i className={`${s.icon} text-base`} style={{ color: s.color }} />
                    </div>
                  </div>
                  <h3 className="font-orbitron font-bold text-sm text-white mb-1">{s.title}</h3>
                  <p className="font-mono text-xs text-white/40 leading-relaxed">{s.desc}</p>
                </div>
                {i < deliverySteps.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-2 z-10 w-4 h-4 items-center justify-center -translate-y-1/2">
                    <i className="ri-arrow-right-s-line text-white/20 text-lg" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Interactive Map ── */}
      <DeliveryMap />

      {/* ── Delivery Calculator ── */}
      <DeliveryCalculator />

      {/* ── Delivery zones ── */}
      <section className="px-6 lg:px-12 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10">
            <p className="font-mono text-xs tracking-[0.3em] text-neon-cyan/60 mb-2">СПОСОБЫ ДОСТАВКИ</p>
            <h2 className="font-orbitron font-black text-2xl md:text-3xl text-white">ВЫБЕРИТЕ УДОБНЫЙ ВАРИАНТ</h2>
            <div className="mt-2 w-12 h-0.5" style={{ background: 'linear-gradient(90deg, #ff2d78, transparent)' }} />
          </div>

          {/* Zone tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {deliveryZones.map((z) => (
              <button
                key={z.id}
                onClick={() => setActiveZone(z.id)}
                className="whitespace-nowrap flex items-center gap-2 px-4 py-2.5 rounded-full font-mono text-xs cursor-pointer transition-all duration-300"
                style={
                  activeZone === z.id
                    ? { background: z.color, color: '#050508', boxShadow: `0 0 14px ${z.color}60` }
                    : { border: `1px solid rgba(255,255,255,0.1)`, color: 'rgba(255,255,255,0.45)' }
                }
              >
                <i className={`${z.icon} text-sm`} />
                {z.title}
              </button>
            ))}
          </div>

          {/* Active zone detail */}
          <div
            className="rounded-2xl p-6 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-8 transition-all duration-300"
            style={{ background: `${activeZoneData.color}06`, border: `1px solid ${activeZoneData.color}25` }}
          >
            {/* Left: main info */}
            <div className="lg:col-span-2">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl shrink-0" style={{ background: `${activeZoneData.color}15`, border: `1px solid ${activeZoneData.color}40` }}>
                  <i className={`${activeZoneData.icon} text-2xl`} style={{ color: activeZoneData.color, textShadow: `0 0 12px ${activeZoneData.color}` }} />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-orbitron font-black text-xl text-white">{activeZoneData.title}</h3>
                    <span className="px-2.5 py-0.5 rounded-full font-mono text-[10px] font-bold" style={{ background: activeZoneData.color, color: '#050508' }}>
                      {activeZoneData.badge}
                    </span>
                  </div>
                  <p className="font-mono text-sm" style={{ color: activeZoneData.color }}>{activeZoneData.subtitle}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <p className="font-mono text-xs text-white/30 mb-1">Стоимость</p>
                  <p className="font-orbitron font-black text-xl" style={{ color: activeZoneData.color }}>{activeZoneData.price}</p>
                  {activeZoneData.freeFrom !== null && activeZoneData.freeFrom > 0 && (
                    <p className="font-mono text-xs text-white/30 mt-1">Бесплатно от {activeZoneData.freeFrom} ₽</p>
                  )}
                </div>
                <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <p className="font-mono text-xs text-white/30 mb-1">Время доставки</p>
                  <p className="font-orbitron font-black text-xl text-white">{activeZoneData.time}</p>
                  <p className="font-mono text-xs text-white/30 mt-1">{activeZoneData.hours}</p>
                </div>
              </div>

              <div>
                <p className="font-mono text-xs text-white/30 mb-3 tracking-wider">ЗОНЫ ДОСТАВКИ</p>
                <div className="flex flex-wrap gap-2">
                  {activeZoneData.zones.map((z) => (
                    <span key={z} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-xs" style={{ background: `${activeZoneData.color}10`, border: `1px solid ${activeZoneData.color}25`, color: activeZoneData.color }}>
                      <i className="ri-map-pin-line text-xs" />
                      {z}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: features */}
            <div>
              <p className="font-mono text-xs text-white/30 mb-4 tracking-wider">ВКЛЮЧЕНО В ДОСТАВКУ</p>
              <div className="space-y-3">
                {activeZoneData.features.map((f) => (
                  <div key={f} className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full shrink-0" style={{ background: `${activeZoneData.color}20`, border: `1px solid ${activeZoneData.color}40` }}>
                      <i className="ri-check-line text-[10px]" style={{ color: activeZoneData.color }} />
                    </div>
                    <span className="font-mono text-xs text-white/60">{f}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/catalog"
                className="whitespace-nowrap mt-6 w-full flex items-center justify-center gap-2 py-3 rounded-xl font-orbitron font-bold text-xs tracking-wider cursor-pointer transition-all"
                style={{ background: activeZoneData.color, color: '#050508', boxShadow: `0 0 15px ${activeZoneData.color}50` }}
              >
                <i className="ri-shopping-bag-line" />
                ОФОРМИТЬ ЗАКАЗ
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Tariff table ── */}
      <section className="px-6 lg:px-12 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <p className="font-mono text-xs tracking-[0.3em] text-neon-cyan/60 mb-2">ТАРИФЫ</p>
            <h2 className="font-orbitron font-black text-2xl md:text-3xl text-white">СТОИМОСТЬ ПО ЗОНАМ</h2>
            <div className="mt-2 w-12 h-0.5" style={{ background: 'linear-gradient(90deg, #39ff14, transparent)' }} />
          </div>

          <div className="card-cyber rounded-2xl overflow-hidden" style={{ borderColor: 'rgba(57,255,20,0.15)' }}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ background: 'rgba(57,255,20,0.06)', borderBottom: '1px solid rgba(57,255,20,0.15)' }}>
                    <th className="text-left px-5 py-4 font-orbitron font-bold text-xs text-white/60 tracking-wider">ЗОНА</th>
                    <th className="text-center px-4 py-4 font-orbitron font-bold text-xs tracking-wider whitespace-nowrap" style={{ color: '#ff2d78' }}>
                      <i className="ri-flashlight-line mr-1" />ЭКСПРЕСС
                    </th>
                    <th className="text-center px-4 py-4 font-orbitron font-bold text-xs tracking-wider whitespace-nowrap" style={{ color: '#00f5ff' }}>
                      <i className="ri-truck-line mr-1" />СТАНДАРТ
                    </th>
                    <th className="text-center px-4 py-4 font-orbitron font-bold text-xs tracking-wider whitespace-nowrap" style={{ color: '#39ff14' }}>
                      <i className="ri-store-2-line mr-1" />САМОВЫВОЗ
                    </th>
                    <th className="text-center px-4 py-4 font-orbitron font-bold text-xs tracking-wider whitespace-nowrap" style={{ color: '#f0ff00' }}>
                      <i className="ri-mail-send-line mr-1" />ПОЧТА/СДЭК
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {deliveryTariffs.map((row, i) => (
                    <tr
                      key={row.zone}
                      style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}
                    >
                      <td className="px-5 py-3.5 font-mono text-xs text-white/70">{row.zone}</td>
                      <td className="px-4 py-3.5 text-center font-mono text-xs" style={{ color: row.express === '—' ? 'rgba(255,255,255,0.2)' : '#ff2d78' }}>{row.express}</td>
                      <td className="px-4 py-3.5 text-center font-mono text-xs" style={{ color: row.standard === '—' ? 'rgba(255,255,255,0.2)' : '#00f5ff' }}>{row.standard}</td>
                      <td className="px-4 py-3.5 text-center font-mono text-xs" style={{ color: '#39ff14' }}>{row.pickup}</td>
                      <td className="px-4 py-3.5 text-center font-mono text-xs" style={{ color: row.post === '—' ? 'rgba(255,255,255,0.2)' : '#f0ff00' }}>{row.post}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ── Payment methods ── */}
      <section className="px-6 lg:px-12 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10">
            <p className="font-mono text-xs tracking-[0.3em] text-neon-cyan/60 mb-2">ОПЛАТА</p>
            <h2 className="font-orbitron font-black text-2xl md:text-3xl text-white">СПОСОБЫ ОПЛАТЫ</h2>
            <div className="mt-2 w-12 h-0.5" style={{ background: 'linear-gradient(90deg, #f0ff00, transparent)' }} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paymentMethods.map((m) => (
              <div
                key={m.id}
                className="card-cyber rounded-2xl p-5 group transition-all duration-300 relative overflow-hidden"
                style={{ borderColor: `${m.color}20` }}
              >
                <div className="flex items-start gap-4 mb-3">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl shrink-0" style={{ background: `${m.color}15`, border: `1px solid ${m.color}40` }}>
                    <i className={`${m.icon} text-xl`} style={{ color: m.color, textShadow: `0 0 10px ${m.color}` }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-orbitron font-bold text-xs text-white leading-snug mb-1">{m.title}</h3>
                    {m.instant && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-[9px]" style={{ background: 'rgba(57,255,20,0.1)', border: '1px solid rgba(57,255,20,0.3)', color: '#39ff14' }}>
                        <i className="ri-flashlight-line" />Мгновенно
                      </span>
                    )}
                  </div>
                </div>
                <p className="font-mono text-xs text-white/40 leading-relaxed mb-3">{m.desc}</p>
                <div className="flex flex-wrap gap-1.5">
                  {m.badges.map((b) => (
                    <span key={b} className="px-2 py-0.5 rounded-full font-mono text-[10px]" style={{ background: `${m.color}10`, border: `1px solid ${m.color}25`, color: m.color }}>
                      {b}
                    </span>
                  ))}
                </div>
                <div className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `linear-gradient(90deg, transparent, ${m.color}, transparent)` }} />
              </div>
            ))}
          </div>

          {/* Security note */}
          <div className="mt-6 p-5 rounded-2xl flex items-start gap-4" style={{ background: 'rgba(57,255,20,0.04)', border: '1px solid rgba(57,255,20,0.15)' }}>
            <div className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0" style={{ background: 'rgba(57,255,20,0.1)', border: '1px solid rgba(57,255,20,0.3)' }}>
              <i className="ri-shield-check-line text-lg" style={{ color: '#39ff14' }} />
            </div>
            <div>
              <h4 className="font-orbitron font-bold text-sm text-white mb-1">Безопасность платежей</h4>
              <p className="font-mono text-xs text-white/40 leading-relaxed">
                Все онлайн-платежи защищены протоколом SSL/TLS и технологией 3D-Secure. Мы не храним данные банковских карт. Платёжный шлюз сертифицирован по стандарту PCI DSS.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="px-6 lg:px-12 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10">
            <p className="font-mono text-xs tracking-[0.3em] text-neon-cyan/60 mb-2">ВОПРОСЫ И ОТВЕТЫ</p>
            <h2 className="font-orbitron font-black text-2xl md:text-3xl text-white">ЧАСТЫЕ ВОПРОСЫ</h2>
            <div className="mt-2 w-12 h-0.5" style={{ background: 'linear-gradient(90deg, #ff2d78, transparent)' }} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {faqItems.map((item) => (
              <FaqItem key={item.q} item={item} />
            ))}
          </div>

          {/* CTA */}
          <div
            className="mt-10 p-6 md:p-8 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-6"
            style={{ background: 'linear-gradient(135deg, rgba(255,45,120,0.07) 0%, rgba(0,245,255,0.05) 100%)', border: '1px solid rgba(255,45,120,0.2)' }}
          >
            <div>
              <h3 className="font-orbitron font-bold text-lg text-white mb-1">Остались вопросы?</h3>
              <p className="font-mono text-xs text-white/40">Наша служба поддержки работает круглосуточно, 24/7</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href="tel:84996536277"
                className="whitespace-nowrap flex items-center gap-2 px-6 py-3 rounded-full font-orbitron font-bold text-xs tracking-wider cursor-pointer transition-all"
                style={{ background: '#ff2d78', color: '#fff', boxShadow: '0 0 15px rgba(255,45,120,0.4)' }}
              >
                <i className="ri-phone-line" />
                8 499 653 6 277
              </a>
              <Link
                to="/contacts"
                className="whitespace-nowrap flex items-center gap-2 px-6 py-3 rounded-full font-orbitron font-bold text-xs tracking-wider cursor-pointer transition-all"
                style={{ border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)' }}
              >
                <i className="ri-message-3-line" />
                Написать нам
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DeliveryPage;
